/**
 * ResultsDashboard Component
 *
 * Optimization runner + results viewer with sub-tabs:
 * Summary, Dispatch, UL 3141, Financial.
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Tabs,
  Tab,
} from '@mui/material';
import { PlayArrow as PlayIcon } from '@mui/icons-material';
import { vortxColors, vortxTypography } from '@vortx/shared-ui';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useAppOptimization } from '../../hooks/useAppOptimization';

const SUB_TABS = [
  { key: 'summary', label: 'Summary' },
  { key: 'dispatch', label: 'Dispatch' },
  { key: 'ul3141', label: 'UL 3141' },
  { key: 'financial', label: 'Financial' },
];

function KPICard({ label, value, unit }) {
  return (
    <Box sx={{ p: 2, border: `1px solid ${vortxColors.teal}`, borderRadius: 1 }}>
      <Typography variant="caption" sx={{ color: vortxColors.stone }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ color: vortxColors.gold, fontWeight: 700 }}>
        {value !== null && value !== undefined ? `${value}${unit || ''}` : 'N/A'}
      </Typography>
    </Box>
  );
}

function SummaryTab({ results }) {
  if (!results) return null;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={3}>
        <KPICard
          label="Annual Energy Cost"
          value={results.annual_cost ? `$${Number(results.annual_cost).toLocaleString()}` : null}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <KPICard
          label="Energy Savings"
          value={results.savings_pct ? `${results.savings_pct.toFixed(1)}%` : null}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <KPICard
          label="PUE"
          value={results.pue ? results.pue.toFixed(2) : null}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <KPICard
          label="Uptime"
          value={results.uptime_pct ? `${results.uptime_pct.toFixed(3)}%` : null}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <KPICard
          label="UL 3141 Score"
          value={results.ul3141_score ? results.ul3141_score.toFixed(1) : null}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <KPICard
          label="Peak Demand"
          value={results.peak_demand_kw ? `${Number(results.peak_demand_kw).toLocaleString()} kW` : null}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <KPICard
          label="Carbon Intensity"
          value={results.carbon_kg_per_mwh ? `${results.carbon_kg_per_mwh.toFixed(0)} kg/MWh` : null}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <KPICard
          label="BESS Cycles/yr"
          value={results.bess_cycles_per_year ? results.bess_cycles_per_year.toFixed(0) : null}
        />
      </Grid>
    </Grid>
  );
}

function DispatchTab({ results }) {
  const dispatchData = results?.dispatch_profile || [];

  if (dispatchData.length === 0) {
    return (
      <Alert severity="info" sx={{ bgcolor: 'rgba(178, 140, 80, 0.1)', border: `1px solid ${vortxColors.gold}`, color: vortxColors.white }}>
        Run optimization to see dispatch chart.
      </Alert>
    );
  }

  return (
    <Box sx={{ width: '100%', height: 400 }}>
      <ResponsiveContainer>
        <AreaChart data={dispatchData}>
          <CartesianGrid strokeDasharray="3 3" stroke={vortxColors.teal} />
          <XAxis dataKey="hour" stroke={vortxColors.stone} tick={{ fontSize: 11 }} />
          <YAxis stroke={vortxColors.stone} tick={{ fontSize: 11 }} label={{ value: 'kW', angle: -90, position: 'insideLeft', fill: vortxColors.stone }} />
          <Tooltip
            contentStyle={{ backgroundColor: vortxColors.charcoal, border: `1px solid ${vortxColors.gold}` }}
            labelStyle={{ color: vortxColors.white }}
          />
          <Legend />
          <Area type="monotone" dataKey="it_load_kw" stackId="1" stroke={vortxColors.gold} fill={vortxColors.gold} fillOpacity={0.6} name="IT Load" />
          <Area type="monotone" dataKey="cooling_kw" stackId="1" stroke={vortxColors.teal} fill={vortxColors.teal} fillOpacity={0.6} name="Cooling" />
          <Area type="monotone" dataKey="overhead_kw" stackId="1" stroke={vortxColors.stone} fill={vortxColors.stone} fillOpacity={0.4} name="Overhead" />
          <Area type="monotone" dataKey="bess_kw" stackId="1" stroke="#4caf50" fill="#4caf50" fillOpacity={0.5} name="BESS" />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
}

function UL3141Tab({ results }) {
  return (
    <Box>
      <Alert
        severity="info"
        sx={{
          bgcolor: 'rgba(178, 140, 80, 0.1)',
          border: `1px solid ${vortxColors.gold}`,
          color: vortxColors.white,
          '& .MuiAlert-icon': { color: vortxColors.gold },
        }}
      >
        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
          UL 3141 Resilience Analysis
        </Typography>
        <Typography variant="body2">
          Pareto analysis and heatmap views will be populated after running a UL 3141 simulation.
          This evaluates backup capacity, islanding duration, and critical load coverage under
          various outage scenarios.
        </Typography>
      </Alert>
      {results?.ul3141_results && (
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} sm={4}>
            <KPICard label="Island Duration" value={results.ul3141_results.island_hours ? `${results.ul3141_results.island_hours}h` : null} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <KPICard label="Critical Load Coverage" value={results.ul3141_results.critical_load_coverage_pct ? `${results.ul3141_results.critical_load_coverage_pct}%` : null} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <KPICard label="Resilience Score" value={results.ul3141_results.resilience_score} />
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

function FinancialTab({ results }) {
  if (!results) return null;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={3}>
        <KPICard
          label="NPV"
          value={results.npv ? `$${Number(results.npv).toLocaleString()}` : null}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <KPICard
          label="IRR"
          value={results.irr ? `${(results.irr * 100).toFixed(1)}%` : null}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <KPICard
          label="Simple Payback"
          value={results.payback_years ? `${results.payback_years.toFixed(1)} yrs` : null}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <KPICard
          label="LCOE"
          value={results.lcoe ? `$${results.lcoe.toFixed(3)}/kWh` : null}
        />
      </Grid>
    </Grid>
  );
}

function ResultsDashboard({ project }) {
  const { submit, status, results, loading, error, cancel } = useAppOptimization(project?.id);
  const [activeTab, setActiveTab] = useState('summary');

  const handleRunOptimization = async () => {
    try {
      await submit({});
    } catch (err) {
      console.error('Failed to submit DC optimization:', err);
    }
  };

  const getStatusMessage = () => {
    switch (status) {
      case 'submitting': return 'Submitting optimization...';
      case 'pending': return 'Optimization queued...';
      case 'running': return 'Running optimization...';
      case 'complete': return 'Optimization complete!';
      case 'failed':
      case 'error': return 'Optimization failed';
      default: return 'Ready to run';
    }
  };

  const TAB_COMPONENTS = {
    summary: SummaryTab,
    dispatch: DispatchTab,
    ul3141: UL3141Tab,
    financial: FinancialTab,
  };

  const ActiveTabComponent = TAB_COMPONENTS[activeTab];

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          fontFamily: vortxTypography.heading,
          color: vortxColors.white,
          mb: 3,
        }}
      >
        Optimization Results
      </Typography>

      {/* Run Button */}
      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={loading ? <CircularProgress size={20} sx={{ color: '#000' }} /> : <PlayIcon />}
          onClick={handleRunOptimization}
          disabled={loading}
          sx={{
            background: `linear-gradient(135deg, ${vortxColors.gold} 0%, #d4a574 100%)`,
            color: '#000',
            fontWeight: 600,
            fontSize: '1.1rem',
            px: 4,
            py: 1.5,
            '&:hover': {
              background: `linear-gradient(135deg, #d4a574 0%, ${vortxColors.gold} 100%)`,
            },
          }}
        >
          {loading ? 'Running...' : 'Run Optimization'}
        </Button>
        {loading && (
          <Button
            variant="outlined"
            size="large"
            onClick={cancel}
            sx={{
              ml: 2,
              borderColor: vortxColors.gold,
              color: vortxColors.gold,
              '&:hover': {
                borderColor: '#d4a574',
                bgcolor: 'rgba(178, 140, 80, 0.1)',
              },
            }}
          >
            Cancel
          </Button>
        )}
      </Box>

      {/* Status */}
      {status && (
        <Card sx={{ mb: 3, bgcolor: vortxColors.charcoal, border: `1px solid ${vortxColors.teal}` }}>
          <CardContent>
            <Typography variant="h6" sx={{ color: vortxColors.white, mb: 1 }}>
              Status: {getStatusMessage()}
            </Typography>
            {loading && (
              <LinearProgress
                sx={{
                  bgcolor: vortxColors.teal,
                  '& .MuiLinearProgress-bar': { bgcolor: vortxColors.gold },
                }}
              />
            )}
          </CardContent>
        </Card>
      )}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Results Sub-tabs */}
      {results && (
        <Box>
          <Tabs
            value={activeTab}
            onChange={(_, val) => setActiveTab(val)}
            sx={{
              mb: 3,
              '& .MuiTabs-indicator': { backgroundColor: vortxColors.gold },
              '& .MuiTab-root': {
                color: vortxColors.stone,
                '&.Mui-selected': { color: vortxColors.gold },
              },
            }}
          >
            {SUB_TABS.map((tab) => (
              <Tab key={tab.key} value={tab.key} label={tab.label} />
            ))}
          </Tabs>

          <Card sx={{ bgcolor: vortxColors.charcoal, border: `1px solid ${vortxColors.teal}` }}>
            <CardContent>
              {ActiveTabComponent && <ActiveTabComponent results={results} />}
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
}

export default ResultsDashboard;
